import type { ContentLibrary } from "../api/contract";

type Entry = ContentLibrary["entries"][number];

/** Kinds whose items may have a photo and a link (the server's item records). */
const LINKED_KINDS: ReadonlySet<string> = new Set(["service", "project", "item"]);

/** Reading order for an item's text fields; anything else follows. */
const ORDER = ["name", "title", "question", "description", "text", "answer", "body"];
const rank = (name: string) => {
  const at = ORDER.indexOf(name);
  return at < 0 ? ORDER.length : at;
};

/** The list item a selected card shows: the first of its records that belongs to a list. */
export function itemRecord(records: string[] | undefined, entries: Entry[] | undefined): Entry | null {
  if (!records?.length || !entries?.length) return null;
  for (const id of records) {
    const entry = entries.find((candidate) => candidate.id === id);
    if (entry && entry.kind !== "media" && entry.list) return entry;
  }
  return null;
}

/** Whether the item can have a photo and a link of its own. */
export function itemHasLinkAndPhoto(entry: Entry): boolean {
  return LINKED_KINDS.has(entry.kind);
}

/** The text fields a card's form shows, in reading order, then its link when it can have one. */
export function itemFieldNames(entry: Entry): string[] {
  const names = Object.keys(entry.fields).filter((name) => name !== "url").sort((a, b) => rank(a) - rank(b));
  return itemHasLinkAndPhoto(entry) || "url" in entry.fields ? [...names, "url"] : names;
}

export interface ItemDraft {
  fields: Record<string, string>;
  /** The media record for its photo, or null for none. */
  image: string | null;
}

export function itemDraft(entry: Entry): ItemDraft {
  return { fields: Object.fromEntries(itemFieldNames(entry).map((name) => [name, entry.fields[name] ?? ""])), image: entry.image?.id ?? null };
}

/** A text field left empty, which can't be saved; the link may be empty (that removes it). */
export function emptyField(draft: ItemDraft): string | null {
  return Object.entries(draft.fields).find(([name, value]) => name !== "url" && !value.trim())?.[0] ?? null;
}

/** What Save sends: only what changed, with a cleared link or photo as null. Null when nothing changed. */
export function itemPatch(entry: Entry, draft: ItemDraft): Record<string, string | null> | null {
  const set: Record<string, string | null> = {};
  for (const [name, value] of Object.entries(draft.fields)) {
    if (value.trim() === (entry.fields[name] ?? "").trim()) continue;
    set[name] = name === "url" ? value.trim() || null : value.trim();
  }
  if (draft.image !== (entry.image?.id ?? null)) set.image = draft.image;
  return Object.keys(set).length ? set : null;
}
